import { gql, useLazyQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { Server } from '../../apollo/types';
import { logger } from '../../datadog';
import { clientUserShortIdAtom, sessionInfoAtom } from '../../state';
import { staleSoftBlockMeasureTypesAtom } from '../state';

export type StaleSoftBlockMeasuresQuery = {
  staleSoftBlockMeasures: MeasureSchemaType[] | null;
};

export type StaleSoftBlockMeasuresQueryVariables = {
  clientUserShortId: string;
};

export const GET_STALE_SOFT_BLOCK_MEASURES = gql`
  query StaleSoftBlockMeasures($clientUserShortId: ID!) {
    staleSoftBlockMeasures(clientUserShortId: $clientUserShortId)
  }
`;

// Soft-block eligibility is purely additive, so we never let a slow or hung
// resolve delay the client reaching their session. If the monolith hasn't
// answered within this window we fail open (no stale measures → no soft block).
const RESOLUTION_TIMEOUT_MS = 3_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('stale soft-block measures resolution timed out')), ms),
    ),
  ]);
}

/**
 * Returns a callback that resolves the soft-block-eligible measure types (a
 * subset of GAD-7 / PHQ-9 / PHQ-9A the client hasn't completed within the
 * staleness window) and stores them in `staleSoftBlockMeasureTypesAtom`.
 *
 * Run during the pre-render gather phase, before the routing decision, so the
 * decision can read a resolved value rather than racing an in-flight query. The
 * monolith round-trip is skipped for clients the BE already deemed ineligible,
 * and a timeout / error fails open so the soft block can never block care.
 */
export function useResolveStaleSoftBlockMeasuresCallback() {
  const setStaleMeasureTypes = useSetAtom(staleSoftBlockMeasureTypesAtom);
  const getIsServerEligible = useAtomCallback(
    useCallback((get) => get(sessionInfoAtom)?.softBlock?.isServerEligible, []),
  );
  const getClientUserShortId = useAtomCallback(useCallback((get) => get(clientUserShortIdAtom), []));
  const [fetchStaleMeasures] = useLazyQuery<StaleSoftBlockMeasuresQuery, StaleSoftBlockMeasuresQueryVariables>(
    GET_STALE_SOFT_BLOCK_MEASURES,
    { context: { server: Server.MONOLITH }, fetchPolicy: 'network-only' },
  );

  return useCallback(async () => {
    let clientUserShortId = '';
    try {
      clientUserShortId = getClientUserShortId();
      if (!getIsServerEligible() || !clientUserShortId) {
        return;
      }

      const result = await withTimeout(fetchStaleMeasures({ variables: { clientUserShortId } }), RESOLUTION_TIMEOUT_MS);
      if (result.error) throw result.error;
      setStaleMeasureTypes(result.data?.staleSoftBlockMeasures ?? []);
    } catch (error) {
      logger.error('Error resolving stale soft-block measures', { clientUserShortId, error });
      setStaleMeasureTypes([]);
    }
  }, [fetchStaleMeasures, getClientUserShortId, getIsServerEligible, setStaleMeasureTypes]);
}
